/* eslint-disable react-hooks/refs, react-hooks/set-state-in-effect, react-hooks/immutability -- focus restoration and debounced search state intentionally cross this component boundary. */
import React, { useState, useCallback, useEffect, useRef, Fragment } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import Button from 'reactstrap/lib/Button';
import { noop, isNull } from 'lodash-es';
import { FocusScope, FocusRing } from '@react-aria/focus';
/* Utils */
import {
  fireAbandonTracking,
  fireResetTracking,
  fireSearchLinkTracking,
  fireSelectAutocompleteTracking,
  fireSubmitTracking,
  fireTemporaryKeydownTracking,
} from 'site-modules/shared/utils/inventory/global-search-tracking';
import { ApiMetrics, withMetrics } from 'client/utils/metrics-hoc';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { buildAiAssistantUrl } from 'site-modules/shared/utils/ai-assistant-url';
import { PageModel } from 'client/data/models/page';
/* Hooks */
import { useDebounce } from 'site-modules/shared/hooks/use-debounce';
import { useGlobalSearchResults } from 'site-modules/shared/hooks/global-search/use-global-search-results';
import { useClickOutside } from 'site-modules/shared/hooks/use-click-outside';
import { useEscPressed } from 'site-modules/shared/hooks/global-search/use-esc-pressed';
import { useRestoreFocusToElement } from 'site-modules/shared/components/inventory/global-search/use-restore-focus-to-element';
import { useTextAreaCollapsedState } from 'site-modules/shared/components/inventory/global-search/use-text-area-collapsed-state';
/* Constants */
import {
  AUTOSIZED_AREA_LENGTH,
  CLEAR_INPUT,
  CREATIVE_ID,
  GLOBAL_SEARCH_TRANSITION_DURATION,
  INPUT_DELAY,
  INPUT_MIN_HEIGHT,
  LINK_COMPONENTS,
  LISTBOX_ID,
  LOOKING_FOR_PLACEHOLDER,
  OPEN_DELAY,
  PRIMING_BOX_COPY,
} from 'site-modules/shared/constants/global-search/global-search';
import { ENTER_KEY_CODE } from 'site-modules/shared/constants/key-codes';
import { TrackingConstant } from 'client/tracking/constant';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
/* Components */
import { HeightCollapseTransition } from 'site-modules/shared/components/inventory/global-search/height-collapse-transition/height-collapse-transition';
import { venomHistory } from 'client/utils/history/venom-history';
import { GlobalSearchDropdown } from 'site-modules/shared/components/inventory/global-search/global-search-dropdown/global-search-dropdown';
import { GlobalSearchDrawer } from 'site-modules/shared/components/inventory/global-search/global-search-drawer/global-search-drawer';
import { AutosizedArea } from 'site-modules/shared/components/inventory/autosized-area/autosized-area';
import { IconMagicSearch } from 'site-modules/shared/components/icon-magic-search/icon-magic-search';
import { useCoDriverScope } from 'site-modules/shared/hooks/use-co-driver-scope';

import './global-search.scss';

const getDefaultInitialRestoreFocusElement = ({ inputContainer, input, isMobile }) =>
  isMobile ? inputContainer : input;

function GlobalSearchUI({
  isMobile = false,
  searchId = '',
  apiMetrics = {},
  onSearchChange = noop,
  withLlmSearchDisabled = false,
  controlled = false,
  isOpen: isOpenProp = false,
  onChangeOpenState = noop,
  creativeId = CREATIVE_ID,
  keepClearButtonPermanently = false,
  trapFocus = undefined,
  inputClassname = '',
  isSearchVisible = true,
  isGlobalNav = false,
  closeOnResultSelect = false,
  visitorLocation = undefined,
  restoreFocusElement: restoreFocusElementProp = undefined,
  query: queryProp = '',
  trackingValue = LOOKING_FOR_PLACEHOLDER,
  formClassName = undefined,
  location,
}) {
  const [isOpenState, setIsOpenState] = useState(false);
  const [query, setQuery] = useState(queryProp);
  const searchContainerRef = useRef();
  const autosizedAreaRef = useRef();
  const inputContainerRef = useRef();

  const initialRestoreFocusElement =
    restoreFocusElementProp ||
    getDefaultInitialRestoreFocusElement({
      inputContainer: inputContainerRef.current,
      input: autosizedAreaRef.current,
      isMobile,
    });

  const restoreFocusElement = useRef(initialRestoreFocusElement);

  const isOpen = controlled ? isOpenProp : isOpenState;

  useEffect(() => {
    if (isOpen) {
      restoreFocusElement.current = initialRestoreFocusElement;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useRestoreFocusToElement({ restore: !isOpen, element: restoreFocusElement.current });
  const textAreaCollapseProps = useTextAreaCollapsedState({ collapsed: !isOpen });

  const setIsOpen = useCallback(
    isOpenSearch => {
      // eslint-disable-next-line no-use-before-define
      debouncedSetIsOpen.cancel();

      onChangeOpenState(isOpenSearch);

      if (!controlled) {
        setIsOpenState(isOpenSearch);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [controlled, onChangeOpenState]
  );

  const debouncedSetIsOpen = useDebounce(setIsOpen, OPEN_DELAY);
  const [animating, setAnimating] = useState(false);
  const trimmedQuery = query?.trim();

  const { results: fastMatcherData, isLoading: isFastMatcherLoading } = useGlobalSearchResults(
    trimmedQuery,
    {
      apiMetrics,
      delay: INPUT_DELAY,
      isOpen,
    },
    visitorLocation
  );

  useEffect(() => {
    setQuery(queryProp);
  }, [queryProp]);

  const closeGlobalSearch = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  useEffect(() => {
    if (isGlobalNav && !isMobile && isSearchVisible) {
      setTimeout(() => {
        autosizedAreaRef.current?.focus();
      }, GLOBAL_SEARCH_TRANSITION_DURATION);
    }
  }, [isSearchVisible, isGlobalNav, isMobile]);

  const isSearchVisibleOnTheScreen = isOpen && isSearchVisible;

  const handleInputChange = useCallback(
    async value => {
      debouncedSetIsOpen(true);
      setAnimating(true);
      setQuery(value);
      onSearchChange(value);
    },
    [debouncedSetIsOpen, onSearchChange]
  );

  const handleInputFocus = useCallback(() => {
    if (isMobile) {
      setIsOpen(true);
    }
  }, [isMobile, setIsOpen]);

  const handleClick = useCallback(() => {
    setIsOpen(true);
    setAnimating(true);
  }, [setIsOpen]);

  const handleDialogClose = useCallback(() => {
    closeGlobalSearch();
    fireAbandonTracking({ input: trimmedQuery, fastMatcherData, creativeId });
  }, [closeGlobalSearch, trimmedQuery, fastMatcherData, creativeId]);

  const trackSearchSubmit = useCallback(() => {
    fireSubmitTracking({
      input: query,
      creativeId,
      searchId: '',
    });
  }, [creativeId, query]);

  const isInCoDriver = useCoDriverScope();

  const handleLinkClick = useCallback(
    ({ linkType, url, event }) => {
      debouncedSetIsOpen.cancel();

      if (closeOnResultSelect) {
        restoreFocusElement.current = null;
        closeGlobalSearch();
      }

      if (linkType === LINK_COMPONENTS.SEARCH) {
        trackSearchSubmit();
      }

      if (isInCoDriver) {
        event?.preventDefault();
        window.top.location.href = url || event.currentTarget?.href;
      }
    },
    [closeGlobalSearch, closeOnResultSelect, debouncedSetIsOpen, isInCoDriver, trackSearchSubmit]
  );

  const handleKeyDown = useCallback(
    event => {
      fireTemporaryKeydownTracking({ event, creativeId });

      if (event.keyCode !== ENTER_KEY_CODE || withLlmSearchDisabled) {
        return;
      }

      event.preventDefault();

      fireSearchLinkTracking({
        input: trimmedQuery,
        fastMatcherData,
        value: `Ask AI ${trimmedQuery}`,
        selectionType: 'codriver',
        creativeId,
      });

      const url = buildAiAssistantUrl(location, query ? { message: query } : {});

      handleLinkClick({ linkType: LINK_COMPONENTS.SEARCH, url });

      if (!isInCoDriver) {
        venomHistory.push(url);
      }
    },
    [creativeId, fastMatcherData, handleLinkClick, isInCoDriver, location, query, trimmedQuery, withLlmSearchDisabled]
  );

  const onQueryCancel = useCallback(() => {
    setQuery('');
  }, []);

  const handleQueryCancel = useCallback(() => {
    onQueryCancel();
    autosizedAreaRef.current?.focus();
    setIsOpen(true);
    fireResetTracking({
      input: trimmedQuery,
      fastMatcherData,
      creativeId,
      ...(keepClearButtonPermanently ? { searchId } : {}),
    });
  }, [onQueryCancel, setIsOpen, trimmedQuery, fastMatcherData, creativeId, keepClearButtonPermanently, searchId]);

  const handleClickOutside = useCallback(() => {
    restoreFocusElement.current = null;
    handleDialogClose();
  }, [handleDialogClose]);

  useClickOutside(searchContainerRef, handleClickOutside, {
    isEnabled: isSearchVisibleOnTheScreen && !isMobile,
    useBubbling: true,
  });

  useEscPressed({ isEnabled: isOpen, handler: handleDialogClose });

  useEffect(() => {
    if (isSearchVisibleOnTheScreen) {
      fireSelectAutocompleteTracking({
        eventType: TrackingConstant.EVENT_TYPE_ACTION_START,
        value: trackingValue,
        creativeId,
      });
      fireSelectAutocompleteTracking({ eventType: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS, creativeId });
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isSearchVisibleOnTheScreen]);

  const formContainer = (
    <div
      className={classnames('global-search-form-container', formClassName, {
        'w-100 mobile': isMobile,
        'is-open': isOpen,
        'pos-a': !isGlobalNav,
        'pos-r': isGlobalNav,
        animating: animating && !isGlobalNav,
      })}
      tabIndex={-1}
      ref={inputContainerRef}
      data-testid="input-container"
    >
      <div className="bg-white global-search-form-wrapper">
        <div className="global-search-form pos-r container px-0">
          <div className="pos-r">
            <div className="search-icon pos-a d-flex justify-content-center align-items-center h-100">
              <IconMagicSearch classNames="size-24" />
            </div>
            <AutosizedArea
              className={classnames(
                'global-search-input size-16',
                {
                  'hide-caret': isMobile && isOpen,
                },
                inputClassname
              )}
              onChange={handleInputChange}
              onClick={handleClick}
              onKeyDown={handleKeyDown}
              hintText={LOOKING_FOR_PLACEHOLDER}
              inputValue={query}
              inputMinHeight={INPUT_MIN_HEIGHT}
              maxLength={AUTOSIZED_AREA_LENGTH}
              innerInputRef={autosizedAreaRef}
              disableOutline
              {...(isGlobalNav
                ? {}
                : {
                    role: 'combobox',
                    'aria-expanded': isOpen,
                    'aria-haspopup': 'dialog',
                    'aria-controls': LISTBOX_ID,
                  })}
              aria-label="Search:"
              onFocus={handleInputFocus}
              withShadow
              {...(isMobile ? { isCollapsed: true } : textAreaCollapseProps)}
            />
          </div>
          <Button
            onClick={handleQueryCancel}
            className={classnames(
              'query-cancel-button p-0 border-0 background-none d-flex justify-content-center align-items-center pos-a',
              { 'd-none': !(!!query && (isOpen || keepClearButtonPermanently)) }
            )}
          >
            <i className="text-cool-gray-50 icon-cross2 small" role="img" aria-label={CLEAR_INPUT} />
          </Button>
          {!isMobile && (
            <HeightCollapseTransition isOpen={isOpen} timeout={GLOBAL_SEARCH_TRANSITION_DURATION}>
              <div
                id={LISTBOX_ID}
                {...(isGlobalNav
                  ? {}
                  : {
                      role: 'dialog',
                    })}
                aria-label={PRIMING_BOX_COPY}
              >
                <GlobalSearchDropdown
                  isOpen={isSearchVisibleOnTheScreen}
                  searchQuery={query}
                  onCloseButtonClick={handleDialogClose}
                  fastMatcherData={fastMatcherData}
                  isFastMatcherLoading={isFastMatcherLoading}
                  searchId={searchId}
                  withLlmSearchDisabled={withLlmSearchDisabled}
                  creativeId={creativeId}
                  isGlobalNav={isGlobalNav}
                  onLinkClick={handleLinkClick}
                  isError={isNull(fastMatcherData)}
                />
              </div>
            </HeightCollapseTransition>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <Fragment>
      {!isMobile && isSearchVisibleOnTheScreen && (
        <div
          className={classnames('global-search-overlay top-0 right-0 bottom-0 left-0', {
            show: isSearchVisibleOnTheScreen,
          })}
          data-testid="page-overlay"
        />
      )}
      <div
        className={classnames('global-search d-flex', {
          'pos-r w-100 mb-1_5 justify-content-center px-1': !isGlobalNav,
        })}
        ref={searchContainerRef}
        data-tracking-parent={creativeId}
      >
        {!isMobile && (
          <FocusScope contain={isSearchVisibleOnTheScreen}>
            <FocusRing within focusRingClass="focus-visible">
              {formContainer}
            </FocusRing>
          </FocusScope>
        )}
        {isMobile && (
          <Fragment>
            {!isGlobalNav && formContainer}
            <GlobalSearchDrawer
              id={LISTBOX_ID}
              ariaLabel={PRIMING_BOX_COPY}
              searchId={searchId}
              query={query}
              creativeId={creativeId}
              fastMatcherData={fastMatcherData}
              onInputChange={handleInputChange}
              onQueryCancel={handleQueryCancel}
              onDrawerClose={handleDialogClose}
              onSearchFieldKeydown={handleKeyDown}
              isFastMatcherLoading={isFastMatcherLoading}
              isOpen={isSearchVisibleOnTheScreen}
              withLlmSearchDisabled={withLlmSearchDisabled}
              isMobile
              trapFocus={trapFocus}
              onLinkClick={handleLinkClick}
              restoreFocus={isGlobalNav}
              isError={isNull(fastMatcherData)}
            />
          </Fragment>
        )}
      </div>
    </Fragment>
  );
}

GlobalSearchUI.propTypes = {
  isMobile: PropTypes.bool,
  query: PropTypes.string,
  searchId: PropTypes.string,
  apiMetrics: ApiMetrics,
  withLlmSearchDisabled: PropTypes.bool,
  onSearchChange: PropTypes.func,
  controlled: PropTypes.bool,
  isOpen: PropTypes.bool,
  keepClearButtonPermanently: PropTypes.bool,
  onChangeOpenState: PropTypes.func,
  creativeId: PropTypes.string,
  trapFocus: PropTypes.bool,
  isSearchVisible: PropTypes.bool,
  isGlobalNav: PropTypes.bool,
  inputClassname: PropTypes.string,
  closeOnResultSelect: PropTypes.bool,
  visitorLocation: VisitorEntities.Location,
  trackingValue: PropTypes.string,
  restoreFocusElement: PropTypes.shape({}),
  formClassName: PropTypes.string,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
};

export const GlobalSearch = withMetrics(
  connectToModel(GlobalSearchUI, {
    visitorLocation: bindToPath('location', VisitorModel),
    location: bindToPath('location', PageModel, null, false),
  })
);
